import {
  Dialog as DialogCore,
  DialogTitle as DialogTitleCore,
  DialogContent as DialogContentCore,
  IconButton as IconButtonCore,
  LinearProgress as LinearProgressCore,
} from '@material-ui/core';
import * as lib from '~/dependencies';

export const Dialog = lib.styled(DialogCore)`
  .MuiDialog-paper {
    width: 420px;
    max-height: 520px;
    border-radius: 8px;
  }
`;

export const DialogTitle = lib.styled(DialogTitleCore)`
  padding: 12px 16px !important;
  border-bottom: 1px solid #f0f0f0;

  .title {
    font-size: 15px;
    font-weight: 600;
    font-family: Nunito, sans-serif;
  }
`;

export const CloseButton = lib.styled(IconButtonCore)`
  position: absolute !important;
  right: 8px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
`;

export const DialogContent = lib.styled(DialogContentCore)`
  padding: 8px 0 !important;
  overflow-y: auto;
`;

export const FileRow = lib.styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

type FileThumbProps = {
  blocked?: boolean;
};

export const FileThumb = lib.styled.div<FileThumbProps>`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  border: ${({ blocked }: FileThumbProps) =>
    blocked ? '2px solid #e74c3c' : '1px solid #e8e8e8'};
  position: relative;
`;

export const BlockedThumbOverlay = lib.styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(200, 48, 48, 0.45);
  color: #fff;
  pointer-events: none;

  svg {
    font-size: 20px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }
`;

export const RetryOverlay = lib.styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: rgba(200, 48, 48, 0.45);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(200, 48, 48, 0.7);
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -3px;
  }

  svg {
    font-size: 20px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }
`;

export const EyeOverlay = lib.styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.28);
  border: none;
  cursor: pointer;
  padding: 0;
  color: #fff;
  opacity: 0;
  transition: opacity 0.15s;

  ${FileThumb}:hover & {
    opacity: 1;
  }

  svg {
    font-size: 18px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.5));
  }
`;

export const ImagePreviewDialog = lib.styled(DialogCore)`
  .MuiDialog-paper {
    background: transparent;
    box-shadow: none;
    max-width: 90vw;
    max-height: 90vh;
  }
`;

export const ImagePreviewImg = lib.styled.img`
  max-width: 90vw;
  max-height: 90vh;
  display: block;
  border-radius: 6px;
`;

export const FileThumbImage = lib.styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const FileBadge = lib.styled.div`
  width: 100%;
  height: 100%;
  background: #f0f4fa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: #2e4a73;
  letter-spacing: 0.3px;
`;

export const FileInfo = lib.styled.div`
  flex: 1;
  min-width: 0;
`;

export const FileName = lib.styled.span`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #222;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FileSize = lib.styled.span`
  display: block;
  font-size: 11px;
  color: #888;
  margin-top: 1px;
`;

export const RemoveButton = lib.styled(IconButtonCore)`
  flex-shrink: 0 !important;
  padding: 6px !important;
  color: #aaa !important;
  margin-top: 2px !important;

  &:hover {
    color: #ff6666 !important;
  }
`;

export const ProgressBar = lib.styled(LinearProgressCore)`
  margin-top: 6px !important;
  border-radius: 3px !important;
  height: 4px !important;
  background-color: #e8e8e8 !important;

  .MuiLinearProgress-bar {
    background-color: #2e6fdb !important;
  }
`;

export const ProgressLabel = lib.styled.span`
  display: block;
  font-size: 11px;
  color: #2e6fdb;
  margin-top: 3px;
`;

export const ModerationLabel = lib.styled.span`
  display: block;
  font-size: 10px;
  color: #888;
  margin-top: 2px;
  opacity: 0;

  @keyframes fade-in {
    to { opacity: 1; }
  }

  animation: fade-in 0.4s ease forwards;
  animation-delay: 3s;
`;

export const ProcessingDots = lib.styled.span`
  @keyframes ellipsis {
    0%   { content: '.'; }
    33%  { content: '..'; }
    66%  { content: '...'; }
  }

  &::after {
    content: '.';
    animation: ellipsis 1s steps(1, end) infinite;
  }
`;

export const SuccessRow = lib.styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;

  svg {
    font-size: 14px;
    color: #27ae60;
    flex-shrink: 0;
  }
`;

export const UuidLabel = lib.styled.span`
  font-size: 10px;
  color: #888;
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ErrorRow = lib.styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  margin-top: 4px;
  min-width: 0;

  svg {
    font-size: 14px;
    line-height: 1;
    color: #e74c3c;
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

export const ErrorLabel = lib.styled.span`
  font-size: 11px;
  line-height: 1.35;
  color: #e74c3c;
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const DialogFooter = lib.styled.div`
  padding: 10px 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
`;

export const DoneButton = lib.styled.button`
  background: #2e6fdb;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 7px 20px;
  font-size: 13px;
  font-weight: 600;
  font-family: Nunito, sans-serif;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #2460c0;
  }

  &:disabled {
    background: #b0c4e8;
    cursor: not-allowed;
  }
`;
